import { USAGE_ENDPOINT } from '../client/usage-recorder';

/**
 * Vanilla-JS inline script for routes that skip client hydration (homepage, careers
 * pages — see StaticInspectorMount.tsx). Mirrors two hydrated-route behaviors that
 * would otherwise need React:
 *   - usage recording: `<Msg>`'s recordUsage() call lives inside a `useEffect`, which
 *     never runs without hydration, so markers are baked into the SSR HTML but never
 *     reported. This walks the DOM once on load instead.
 *   - Alt+Click-to-copy (id) / Shift+Alt+Click-to-copy (rendered text): same
 *     delegated-listener approach as InspectorOverlay.tsx, reusing the same
 *     `.i18n-inspect-flash` class for the copy confirmation.
 *   - the outline CSS (OVERLAY_STYLE) is gated on a `.i18n-inspect-on` ancestor class,
 *     normally toggled on `<body>` by InspectorOverlay's `useEffect` — which, again,
 *     never runs here. Added to `<html>` instead (a CSS descendant selector matches
 *     any ancestor, not just a direct parent, so this is equivalent).
 *
 * Only ever rendered when the inspector is enabled (StaticInspectorMount returns null
 * otherwise), so this never reaches a normal visitor's page.
 */
export function buildStaticInspectorScript(): string {
  return `(function(){
document.documentElement.classList.add('i18n-inspect-on');
try{
  var route=window.location.pathname;
  var els=document.querySelectorAll('[data-i18n-inspectable]');
  var seen={},entries=[];
  for(var i=0;i<els.length;i++){
    var id=els[i].getAttribute('data-i18n-id');
    if(!id||seen[id])continue;
    seen[id]=true;
    entries.push({id:id,route:route});
  }
  if(entries.length>0){
    fetch('${USAGE_ENDPOINT}',{
      method:'POST',
      credentials:'same-origin',
      headers:{'Content-Type':'application/json'},
      body:JSON.stringify({entries:entries}),
    }).catch(function(){});
  }
}catch(e){}
document.addEventListener('click',function(e){
  if(!e.altKey)return;
  var marker=e.target&&e.target.closest&&e.target.closest('[data-i18n-inspectable]');
  if(!marker)return;
  var id=marker.getAttribute('data-i18n-id');
  if(!id)return;
  var text=e.shiftKey?(marker.textContent||'').trim():id;
  if(!text)return;
  e.preventDefault();
  e.stopPropagation();
  if(navigator.clipboard)navigator.clipboard.writeText(text).catch(function(){});
  marker.classList.add('i18n-inspect-flash');
  setTimeout(function(){marker.classList.remove('i18n-inspect-flash');},1600);
},true);
})();`;
}
